import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { useCurrentUser, useView } from '@/hooks';
import {
  isMomentsSession,
  NoteOwnerType,
  type Session,
  type SessionFeedback,
  SessionStatus,
  SessionWarningAction,
  type User,
} from '@/types';
import { Button, Icon, Paper, Typography } from '@/ui';

import Detail from '../../components/Session';
import SessionCardSekeleton from '../../components/Sessions/Card/Skeleton';

import type {
  MemberSessionArtifactSummary,
  MemberSessionTranscriptSummary,
} from '@/components/Session/MemberSessionDetailRedesign';
import type { MomentsSessionReadItem } from '@/store/api/momentsApi';

import CancelSession from '@/components/Cal/cancelSession';
import RescheduleSession from '@/components/Cal/rescheduleSession';
import ScheduleSession from '@/components/Cal/scheduleSession';
import Feedback from '@/components/Feedback';
import Notes from '@/components/Notes/Notes';
import MemberSessionDetailRedesign, {
  canJoinMemberSession,
} from '@/components/Session/MemberSessionDetailRedesign';
import SessionWarning from '@/components/Session/SessionWarning';
import { SessionAction } from '@/constants/sessions';
import { coachIdentityQueryArg } from '@/features/appointments/coachIdentity';
import {
  legacySubjectPersonId,
  resolveMemberCoachBookingAuthority,
  resolveMemberCoachMatchState,
  resolveQueryAuthorityState,
} from '@/features/appointments/memberCoachBookingState';
import { usesMomentsScheduling } from '@/features/appointments/schedulingProvider';
import { useSessionJoinUrl } from '@/features/appointments/useSessionJoinUrl';
import { sessionNeedsActionWarning } from '@/features/coachSchedule/useSessionActions';
import { api } from '@/store/api';
import { useGetAppointmentsQuery } from '@/store/api/appointmentApi';
import {
  useCreateFeedbackMutation,
  useUpdateFeedbackMutation,
} from '@/store/api/feedbackApi';
import {
  useGetMomentsFeatureFlagForLegacySubjectQuery,
  useGetMomentsSessionReadByLegacyIdQuery,
} from '@/store/api/momentsApi';
import {
  useGetScopedSessionQuery,
  useGetSessionQuery,
  useGetSessionsQuery,
} from '@/store/api/sessionApi';
import { useGetSharedArtifactBySessionQuery } from '@/store/api/sharedArtifactApi';
import { useAppDispatch } from '@/store/hooks';
import { track } from '@/utils/tracking';

const isMemberSelfSessionView = (currentUser: User, session: Session) =>
  !!session.id && currentUser.id === session.userId;

const mapReadArtifactsToRedesign = (
  sessionRead?: MomentsSessionReadItem | null,
): MemberSessionArtifactSummary[] =>
  (sessionRead?.artifacts ?? []).map((artifact) => ({
    artifactId: artifact.artifactId,
    profileSlug: artifact.profileSlug,
    sessionDate: artifact.sessionStartsAt[0] ?? sessionRead?.plannedStartAt,
    title: artifact.title,
    updatedAt: artifact.updatedAt,
  }));

const mapReadAttachmentsToRedesign = (
  sessionRead?: MomentsSessionReadItem | null,
): MemberSessionTranscriptSummary[] =>
  (sessionRead?.attachments ?? [])
    .filter((attachment) => attachment.kind === 'transcript')
    .map((attachment) => ({
      attachmentId: attachment.attachmentId,
      contentStatus: attachment.contentStatus,
      displayName: attachment.displayName,
    }));

const sessionReadHasTranscript = (
  sessionRead?: MomentsSessionReadItem | null,
) => mapReadAttachmentsToRedesign(sessionRead).length > 0;

const previousSessionBoundary = (startedAt?: string) => {
  if (!startedAt) return undefined;
  const startedAtTime = new Date(startedAt).getTime();
  if (Number.isNaN(startedAtTime)) return undefined;
  return new Date(startedAtTime - 1).toISOString();
};

const firstPreviousSessionFromBuckets = (
  buckets: Record<string, Session[]> | undefined,
  currentSession: Partial<Session>,
): Session | undefined => {
  const currentStartedAtTime = currentSession.startedAt
    ? new Date(currentSession.startedAt).getTime()
    : Number.NaN;

  if (!currentSession.id || Number.isNaN(currentStartedAtTime)) {
    return undefined;
  }

  return Object.values(buckets ?? {})
    .flat()
    .filter((session) => {
      const startedAtTime = new Date(session.startedAt).getTime();
      return (
        session.id !== currentSession.id &&
        !Number.isNaN(startedAtTime) &&
        startedAtTime < currentStartedAtTime
      );
    })
    .sort(
      (left, right) =>
        new Date(right.startedAt).getTime() -
        new Date(left.startedAt).getTime(),
    )[0];
};

const SessionPage = () => {
  const dispatch = useAppDispatch();
  const {
    currentData: currentUserData,
    isError: isCurrentUserError,
    isFetching: isCurrentUserFetching,
    isSuccess: isCurrentUserSuccess,
    user: currentUser,
    isUserLoading: isCurrentUserLoading,
  } = useCurrentUser();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { isOverview } = useView();
  const { memberSessionId, sessionId, id: memberId } = useParams();
  const [searchParams] = useSearchParams();
  const id = (memberSessionId ?? sessionId ?? '') as string;
  const feedbackType = searchParams.get('type') ?? '';
  const sessionAction = searchParams.get('action');
  const [openFeedback, setOpenFeedback] = useState<Session | null>();
  const [openCancel, setOpenCancel] = useState<Session | null>(null);
  const [openReschedule, setOpenReschedule] = useState<Session | null>(null);
  const [openSchedule, setOpenSchedule] = useState<User | null>(null);
  const [warningAction, setWarningAction] =
    useState<SessionWarningAction | null>(null);
  const consumedSessionActionRef = useRef<string | null>(null);

  const [createFeedback] = useCreateFeedbackMutation();
  const [updateFeedback] = useUpdateFeedbackMutation();

  let actingRole: 'coach' | 'member' | undefined;
  if (pathname.startsWith('/coach/sessions/')) {
    actingRole = 'coach';
  } else if (pathname.startsWith('/m/sessions/')) {
    actingRole = 'member';
  }
  const scopedSession = useGetScopedSessionQuery(
    { actingRole: actingRole ?? 'member', id },
    { skip: !id || !actingRole },
  );
  const legacySession = useGetSessionQuery(id, { skip: !id || !isOverview });
  const {
    data = {} as Session,
    error,
    isLoading,
  } = isOverview ? legacySession : scopedSession;
  const isCoachViewing = currentUser.id === data.coachId;
  const concerningUser = isCoachViewing ? data.user : data.coach;
  const sessionCoach = data.coach as User | undefined;
  const feedbackCoachId = sessionCoach?.id ?? data.coachId;
  const coachFullProfile = currentUserData?.coaches.find(
    (coach) => coach.id === feedbackCoachId,
  );
  const {
    currentData: currentAppointmentPage,
    isError: isAppointmentsError,
    isFetching: isAppointmentsFetching,
    isSuccess: isAppointmentsSuccess,
  } = useGetAppointmentsQuery(
    currentUser.id
      ? {
          ...(actingRole ? { actingRole } : {}),
          userId: currentUser.id,
        }
      : skipToken,
  );
  const {
    currentData: coachIdentity,
    isError: isCoachIdentityError,
    isFetching: isCoachIdentityFetching,
    isSuccess: isCoachIdentitySuccess,
  } = useGetMomentsFeatureFlagForLegacySubjectQuery(
    coachIdentityQueryArg(feedbackCoachId),
  );
  const { canBookBiweekly } = resolveMemberCoachBookingAuthority(
    {
      appointmentState: resolveQueryAuthorityState({
        hasCurrentData: !!currentAppointmentPage,
        isError: isAppointmentsError,
        isFetching: isAppointmentsFetching,
        isSuccess: isAppointmentsSuccess,
      }),
      appointments: currentAppointmentPage?.appointments ?? [],
      coachPersonId: legacySubjectPersonId(coachIdentity),
      identityState: resolveQueryAuthorityState({
        hasCurrentData: !!coachIdentity,
        isError: isCoachIdentityError,
        isFetching: isCoachIdentityFetching,
        isSuccess: isCoachIdentitySuccess,
      }),
      matchState: resolveMemberCoachMatchState(
        {
          currentCoachIds:
            currentUserData?.coaches.map((coach) => coach.id) ?? [],
          hasCurrentData: !!currentUserData,
          isError: isCurrentUserError,
          isFetching: isCurrentUserFetching,
          isSuccess: isCurrentUserSuccess,
        },
        feedbackCoachId,
      ),
    },
    Date.now(),
  );
  const canScheduleAfterFeedback =
    !!coachFullProfile &&
    (!usesMomentsScheduling(coachFullProfile) || canBookBiweekly);
  const showMemberSessionAssetsView =
    !isCurrentUserLoading &&
    !isCurrentUserError &&
    !!currentUserData &&
    currentUserData.id === currentUser.id &&
    !error &&
    !isLoading &&
    data.id === id &&
    isMemberSelfSessionView(currentUser, data);
  const {
    currentData: momentsSessionRead,
    isError: isMomentsSessionReadError,
    isLoading: isMomentsSessionReadLoading,
  } = useGetMomentsSessionReadByLegacyIdQuery(
    { legacySessionId: data.id },
    { skip: !showMemberSessionAssetsView || !data.id },
  );
  let momentsSessionReadState: 'error' | 'loading' | 'ready' = 'ready';
  if (isMomentsSessionReadLoading) {
    momentsSessionReadState = 'loading';
  } else if (isMomentsSessionReadError) {
    momentsSessionReadState = 'error';
  }
  const priorSessionMaxDate = previousSessionBoundary(data.startedAt);
  const { data: previousSessions } = useGetSessionsQuery(
    {
      ...(actingRole ? { actingRole } : {}),
      coachId: data.coachId,
      limit: 2,
      maxDate: priorSessionMaxDate,
      order: 'desc',
      page: 1,
      statuses: [SessionStatus.DONE],
      userId: data.userId,
    },
    {
      skip:
        !showMemberSessionAssetsView ||
        !data.coachId ||
        !priorSessionMaxDate ||
        !data.userId,
    },
  );
  const previousSession = firstPreviousSessionFromBuckets(
    previousSessions?.items,
    data,
  );
  const { currentData: previousMomentsSessionRead } =
    useGetMomentsSessionReadByLegacyIdQuery(
      { legacySessionId: previousSession?.id ?? '' },
      { skip: !showMemberSessionAssetsView || !previousSession?.id },
    );
  const previousSessionHadTranscript = previousMomentsSessionRead
    ? sessionReadHasTranscript(previousMomentsSessionRead.item)
    : undefined;
  const isNativeMomentsSession = isMomentsSession(data);
  const isNativeMomentsRescheduleSession =
    Boolean(data.sourceAppointmentId) || isNativeMomentsSession;
  const { data: sharedSummary } = useGetSharedArtifactBySessionQuery(
    { session_id: data.id },
    { skip: !data.id },
  );
  const selfSessionsBase =
    actingRole === 'coach' ? '/coach/sessions' : '/m/sessions';
  const sharedSummaryPath =
    memberSessionId && memberId
      ? `/coach/members/${memberId}/sessions/${data.id}/summary`
      : `${selfSessionsBase}/${data.id}/summary`;

  const onViewProfile = () => {
    if (!isCoachViewing) {
      navigate(`/m/profiles-coach/${concerningUser.id}`);
    } else {
      navigate(`/coach/members/${concerningUser.id}`);
    }
  };

  const onGoBack = () => {
    let backUrl;
    if (memberSessionId && !isLoading && memberId) {
      backUrl = `/coach/members/${memberId}/sessions`;
    } else {
      backUrl = selfSessionsBase;
    }
    navigate(backUrl);
  };

  const onUpdate = async (feedback: SessionFeedback) => {
    try {
      if (feedback.id) {
        await updateFeedback({
          id: feedback.id,
          category: feedback.category,
          content: feedback.content,
        }).unwrap();
      } else {
        await createFeedback({
          category: feedback.category,
          content: feedback.content,
          sessionId: feedback.sessionId,
        }).unwrap();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const onClose = () => {
    track('Clicked Dimsiss Feedback', {
      sessionId: openFeedback?.id,
    });
    setOpenFeedback(null);
  };

  const onCloseAndSchedule = () => {
    if (openFeedback) {
      if (coachFullProfile && usesMomentsScheduling(coachFullProfile)) {
        if (!canBookBiweekly) return;
        setOpenFeedback(null);
        navigate(`/m/book-biweekly/${coachFullProfile.id}`);
        return;
      }
      if (coachFullProfile) {
        setOpenSchedule(coachFullProfile);
      }
      setOpenFeedback(null);
    }
  };

  const onCloseSchedule = () => setOpenSchedule(null);

  const joinUrl = useSessionJoinUrl(data, {
    enabled: showMemberSessionAssetsView && !!data.id,
  });
  const onJoinSession = () => {
    const session = {
      endedAt: data.endedAt,
      id: data.id,
      location: joinUrl,
      startedAt: data.startedAt,
      status: data.status,
      title: data.title,
    };
    if (!canJoinMemberSession(session)) {
      return;
    }
    window.open(joinUrl, '_blank', 'noopener,noreferrer');
  };
  const onCancelSession = () => {
    if (!isNativeMomentsSession && sessionNeedsActionWarning(data)) {
      track('Shown Cancel Speedbump', {
        bookId: data.bookId,
        isCoach: false,
        sessionId: data.id,
      });
      setWarningAction(SessionWarningAction.CANCEL);
      return;
    }
    track('Clicked Cancel Session', {
      bookId: data.bookId,
      sessionId: data.id,
    });
    setOpenCancel(data);
  };

  const onRescheduleSession = () => {
    if (!isNativeMomentsRescheduleSession && sessionNeedsActionWarning(data)) {
      track('Shown Reschedule Speedbump', {
        bookId: data.bookId,
        isCoach: false,
        sessionId: data.id,
      });
      setWarningAction(SessionWarningAction.RESCHEDULE);
      return;
    }
    track('Click Reschedule Session', {
      bookId: data.bookId,
      sessionId: data.id,
    });
    setOpenReschedule(data);
  };

  const onCloseSessionAction = () => {
    dispatch(api.util.invalidateTags(['Session']));
    setOpenCancel(null);
    setOpenReschedule(null);
  };
  const onCloseWarning = () => setWarningAction(null);
  const onMemberSessionCancelled = () => {
    setOpenCancel(null);
    navigate(`${selfSessionsBase}/upcoming`, {
      state: { sessionCancelled: true },
    });
  };
  const clearSessionActionParam = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('action');
    const nextSearch = nextParams.toString();
    navigate(
      {
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true },
    );
  };

  useEffect(() => {
    document.title = 'Mento | Coaching sessions';
    return () => {
      document.title = 'Mento';
    };
  }, []);

  useEffect(() => {
    if (!feedbackType || isLoading || isCurrentUserLoading) return;
    setOpenFeedback({ ...data });
  }, [isLoading, feedbackType, isCurrentUserLoading]);

  useEffect(() => {
    if (isLoading || !showMemberSessionAssetsView || !data.id) return;
    if (
      sessionAction !== SessionAction.CANCEL &&
      sessionAction !== SessionAction.RESCHEDULE
    ) {
      return;
    }

    const actionKey = `${data.id}:${sessionAction}`;
    if (consumedSessionActionRef.current === actionKey) return;
    consumedSessionActionRef.current = actionKey;

    if (sessionAction === SessionAction.CANCEL) {
      onCancelSession();
    } else {
      onRescheduleSession();
    }
    clearSessionActionParam();
  }, [data.id, isLoading, sessionAction, showMemberSessionAssetsView]);

  // RTK can retain the previous route's data with isLoading=false while
  // the new session is pending. Do not admit it through shared Detail.
  if (isLoading || (!error && data.id !== id))
    return (
      <div className="min-h-screen overflow-x-hidden">
        <div className="container mx-auto flex max-w-screen-md flex-1 flex-col p-4">
          <Paper
            className="mt-6 flex gap-4 px-8 pb-12 pt-8 "
            variant="outlined"
          >
            <SessionCardSekeleton />
          </Paper>
        </div>
      </div>
    );

  if (error)
    return <Typography>There was an error retrieving this session </Typography>;

  return (
    <div className="min-h-screen overflow-x-hidden">
      <div className="container mx-auto flex max-w-screen-md flex-1 flex-col px-2 py-6 md:px-0">
        <div className="headline flex items-center py-5">
          <Button
            startIcon={<Icon>chevron_left</Icon>}
            variant="text"
            onClick={onGoBack}
          >
            <Typography className="title pl-1 ">Sessions</Typography>
          </Button>
        </div>
        {showMemberSessionAssetsView ? (
          <MemberSessionDetailRedesign
            artifactReadinessReason={
              momentsSessionRead?.item?.artifactGenerationReadiness?.reason
            }
            artifactReadinessStatus={
              momentsSessionRead?.item?.artifactGenerationReadiness?.status
            }
            artifacts={mapReadArtifactsToRedesign(momentsSessionRead?.item)}
            failedArtifactProfiles={
              momentsSessionRead?.item?.failedArtifactProfiles
            }
            member={data.coach}
            notesContent={
              <Notes
                limit={3}
                owneableId={data.id}
                owneableType={NoteOwnerType.SESSION}
              />
            }
            previousSessionHadTranscript={previousSessionHadTranscript}
            previousSessionReadinessReason={
              previousMomentsSessionRead?.item?.artifactGenerationReadiness
                ?.reason
            }
            session={{
              endedAt: data.endedAt,
              id: data.id,
              location: joinUrl,
              momentsSessionId: momentsSessionRead?.item?.sessionId,
              startedAt: data.startedAt,
              status: data.status,
              title: data.title,
            }}
            sessionReadState={momentsSessionReadState}
            sharedBy={data.coach.firstName}
            sharedSummary={sharedSummary}
            sharedSummaryHref={sharedSummaryPath}
            transcripts={mapReadAttachmentsToRedesign(momentsSessionRead?.item)}
            onCancel={onCancelSession}
            onJoin={onJoinSession}
            onReschedule={onRescheduleSession}
          />
        ) : (
          <Detail
            isCoachViewing={isCoachViewing}
            loading={isLoading}
            otherUser={concerningUser}
            session={data}
            hideMenu
            onViewProfile={onViewProfile}
          />
        )}
      </div>
      {!!openFeedback && (
        <Feedback
          session={openFeedback}
          showMaybeLayer
          onClose={onClose}
          onCloseAndSchedule={
            canScheduleAfterFeedback ? onCloseAndSchedule : undefined
          }
          onUpdate={onUpdate}
        />
      )}
      {warningAction && (
        <SessionWarning
          action={warningAction}
          close={onCloseWarning}
          session={data}
          onCancel={setOpenCancel}
          onReschedule={setOpenReschedule}
        />
      )}
      {openCancel && (
        <CancelSession
          allowMemberMomentsCancellation={isNativeMomentsSession}
          close={onCloseSessionAction}
          session={openCancel}
          onCancelled={onMemberSessionCancelled}
          onRescheduleInstead={() => {
            setOpenCancel(null);
            setOpenReschedule(data);
          }}
        />
      )}
      {openReschedule && (
        <RescheduleSession
          close={onCloseSessionAction}
          enableNativeMoments={isNativeMomentsRescheduleSession}
          session={openReschedule}
          onRescheduled={() => navigate(selfSessionsBase)}
        />
      )}
      {openSchedule && (
        <ScheduleSession
          client={currentUser}
          close={onCloseSchedule}
          coach={openSchedule}
        />
      )}
    </div>
  );
};

export default SessionPage;
